import type { ChangeEvent } from 'react';

import Loader from '@components/loader/Loader';
import { Button, Checkbox, TextInput } from '@trussworks/react-uswds';

import type { GenericFilterItem, GenericFilterType, GenericFiltersProps } from './filters.types';
import { sortFilterItemsByLabel } from './filters.mappers';

const FilterCheckbox = <TType extends GenericFilterType>({
  item,
  selected,
  type,
  onChange,
}: {
  item: GenericFilterItem;
  selected: string[];
  type: TType;
  onChange: (e: ChangeEvent<HTMLInputElement>, type: TType) => void;
}) => {
  const checkedValue = item.checkedValue ?? item.value;
  const label = item.label ?? item.value;
  const checkboxId = `${type}-${item.value}`.replace(/\s+/g, '-');

  return (
    <Checkbox
      checked={selected.includes(checkedValue)}
      className='small-checkbox table-filter-checkbox'
      id={checkboxId}
      label={
        <>
          <span className='table-filter-text'>{label}</span>
          <span className='table-filter-count'>{item.count}</span>
        </>
      }
      name={item.value}
      onChange={(e) => onChange(e, type)}
      value={item.value}
    />
  );
};

const Filters = <TType extends GenericFilterType>({
  panelTitle,
  panelTitleClassName = 'table-filter-title',
  loading = false,
  search,
  sections,
}: GenericFiltersProps<TType>) => (
  <div className='table-filters-container'>
    <div className={panelTitleClassName}>{panelTitle}</div>
    <Loader showLoader={loading} variant='page' />
    {loading ? null : (
      <div className='filters-content'>
        {search ? (
          <div className='table-filter-section'>
            <TextInput
              type='text'
              name='table-filter-search'
              id='table-filter-search'
              className='table-filter-text'
              placeholder={search.placeholder ?? 'Search'}
              onChange={search.onChange}
              value={search.value}
            />
          </div>
        ) : null}

        {sections.map((section) => (
          <div className='table-filter-section' key={section.id}>
            <div className='table-filter-title'>
              {section.title}
              {section.selectedValues.length > 0 ? (
                <span className='table-filter-section-count'> : {section.selectedValues.length}</span>
              ) : null}
              {section.showClear && section.onClear ? (
                <Button type='button' className='table-filters-clear-btn' outline onClick={section.onClear}>
                  Clear
                </Button>
              ) : null}
            </div>
            <div className='table-filter-section-content'>
              {[...section.items].sort(sortFilterItemsByLabel).map((item) => (
                <FilterCheckbox
                  key={`${section.id}-${item.value}`}
                  item={item}
                  onChange={section.onToggle}
                  selected={section.selectedValues}
                  type={section.type}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default Filters;
